import { useEffect, useState, useCallback } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { InlineWidget } from "react-calendly";
import { motion } from "framer-motion";
import { CheckCircle2, Loader2, AlertTriangle } from "lucide-react";
import axios from "axios";
import { CALENDLY_URL } from "@/lib/calendly";
import { Logo } from "@/components/Logo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const MAX_POLLS = 10;

export default function PaymentSuccess() {
    const [params] = useSearchParams();
    const sessionId = params.get("session_id");
    const [state, setState] = useState("checking"); // checking | paid | failed | error
    const [service, setService] = useState("");
    const [email, setEmail] = useState("");

    const poll = useCallback(
        async (attempt) => {
            if (!sessionId) {
                setState("error");
                return;
            }
            if (attempt >= MAX_POLLS) {
                setState("failed");
                return;
            }
            try {
                const { data } = await axios.get(
                    `${API}/payments/status/${sessionId}`
                );
                if (data.payment_status === "paid") {
                    setService(data.service || "");
                    setEmail(data.email || "");
                    setState("paid");
                    return;
                }
                if (["failed", "expired"].includes(data.payment_status)) {
                    setState("failed");
                    return;
                }
                setTimeout(() => poll(attempt + 1), 2000);
            } catch (e) {
                setTimeout(() => poll(attempt + 1), 2000);
            }
        },
        [sessionId]
    );

    useEffect(() => {
        poll(0);
    }, [poll]);

    return (
        <div className="min-h-screen bg-[#070A13] text-[#FDFBF7] grain flex flex-col">
            <div className="max-w-3xl mx-auto w-full px-6 py-10">
                <Link to="/" className="flex items-center gap-3">
                    <Logo className="w-9 h-9" dark />
                    <span className="font-display text-xl">The Raw Math</span>
                </Link>
            </div>

            <div className="flex-1 max-w-3xl mx-auto w-full px-6 pb-20">
                {state === "checking" && (
                    <div
                        data-testid="payment-checking"
                        className="text-center py-24"
                    >
                        <Loader2
                            size={40}
                            className="animate-spin mx-auto text-[#C5A059] mb-6"
                        />
                        <p className="font-display text-3xl tracking-tight">
                            Confirming your payment…
                        </p>
                        <p className="font-body text-white/50 mt-3">
                            One moment — don&apos;t close this page.
                        </p>
                    </div>
                )}

                {state === "paid" && (
                    <motion.div
                        data-testid="payment-success"
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6 }}
                    >
                        <div className="text-center mb-10">
                            <span className="inline-flex w-16 h-16 rounded-full bg-[#C5A059] text-[#070A13] items-center justify-center mb-6">
                                <CheckCircle2 size={32} />
                            </span>
                            <h1 className="font-display text-4xl md:text-5xl tracking-tighter mb-4">
                                Payment confirmed.
                            </h1>
                            <p className="font-body text-lg text-white/70 max-w-lg mx-auto">
                                {service ? `Your ${service} is secured — ` : ""}
                                now pick the time that works for you. This is the
                                last step.
                            </p>
                            <p className="font-mono text-[10px] uppercase tracking-[0.15em] text-[#C5A059]/80 mt-6 max-w-lg mx-auto">
                                ⚠ Once booked, a call counts as used. No-shows &
                                late cancellations are not refunded.
                            </p>
                        </div>
                        <div
                            data-testid="success-calendly"
                            className="rounded-xl overflow-hidden border border-white/10 bg-white"
                        >
                            <InlineWidget
                                url={CALENDLY_URL}
                                styles={{ height: "720px" }}
                                pageSettings={{ primaryColor: "d9381e" }}
                                prefill={email ? { email } : undefined}
                            />
                        </div>
                    </motion.div>
                )}

                {(state === "failed" || state === "error") && (
                    <div
                        data-testid="payment-failed"
                        className="text-center py-24"
                    >
                        <AlertTriangle
                            size={40}
                            className="mx-auto text-[#C5A059] mb-6"
                        />
                        <h1 className="font-display text-3xl tracking-tight mb-4">
                            We couldn&apos;t confirm your payment.
                        </h1>
                        <p className="font-body text-white/60 mb-8 max-w-md mx-auto">
                            If you were charged, don&apos;t worry — email
                            hello@theRawMath.com and I&apos;ll sort it out
                            personally. Otherwise you can try again.
                        </p>
                        <Link
                            to="/#pricing"
                            data-testid="failed-back-pricing"
                            className="inline-flex items-center gap-2 bg-[#C5A059] text-[#070A13] font-body font-semibold rounded-full px-8 py-4"
                        >
                            Back to pricing
                        </Link>
                    </div>
                )}
            </div>
        </div>
    );
}
